import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from './Navigation';
import Footer from './Footer';

const UseCaseCard = ({ title, description, industry, results, icon, gradient }) => (
  <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-1">
    <div className={`bg-gradient-to-br ${gradient} rounded-2xl p-8 h-full`}>
      <div className="flex flex-col h-full">
        <div className="flex items-start gap-4 mb-6">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
              {icon}
            </div>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-white mb-2">{title}</h3>
            <p className="text-gray-400 text-sm">Industry: {industry}</p>
          </div>
        </div>
        <p className="text-gray-300 mb-6">{description}</p>
        <div className="mt-auto">
          <h4 className="text-white font-medium mb-2">Results:</h4>
          <ul className="list-disc list-inside text-gray-300 space-y-1">
            {results.map((result, index) => (
              <li key={index}>{result}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </div>
);

const UseCases = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const useCases = [
    {
      title: "Healthcare Appointment Scheduling",
      industry: "Healthcare",
      description: "A large medical practice implemented AI agents to handle appointment scheduling and reminders, reducing no-shows and improving patient engagement.",
      results: [
        "50% reduction in no-show rates",
        "75% decrease in scheduling staff workload",
        "90% patient satisfaction rate"
      ],
      icon: (
        <svg className="w-6 h-6 text-[#FF56A5]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      gradient: "from-[#FF56A5]/20 via-[#E852FF]/20 to-[#9F44F2]/20"
    },
    {
      title: "Real Estate Lead Qualification",
      industry: "Real Estate",
      description: "A national real estate agency deployed AI agents to qualify leads and schedule property viewings, increasing agent efficiency and lead conversion.",
      results: [
        "3x increase in qualified leads",
        "40% reduction in response time",
        "60% increase in viewing appointments"
      ],
      icon: (
        <svg className="w-6 h-6 text-[#E852FF]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      ),
      gradient: "from-[#E852FF]/20 via-[#9F44F2]/20 to-[#FF56A5]/20"
    },
    {
      title: "E-commerce Customer Support",
      industry: "Retail",
      description: "An online retailer implemented AI agents for 24/7 customer support, handling order inquiries, returns, and product recommendations.",
      results: [
        "85% reduction in response time",
        "70% cost savings in support operations",
        "95% issue resolution rate"
      ],
      icon: (
        <svg className="w-6 h-6 text-[#9F44F2]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      gradient: "from-[#9F44F2]/20 via-[#FF56A5]/20 to-[#E852FF]/20"
    },
    {
      title: "Financial Services Outreach",
      industry: "Finance",
      description: "A financial advisory firm used AI agents to conduct personalized outreach campaigns and schedule consultations with qualified prospects.",
      results: [
        "200% increase in consultation bookings",
        "45% higher conversion rate",
        "65% reduction in acquisition costs"
      ],
      icon: (
        <svg className="w-6 h-6 text-[#FF56A5]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      gradient: "from-[#FF56A5]/20 via-[#E852FF]/20 to-[#9F44F2]/20"
    }
  ];

  return (
    <div className="landing-page bg-background min-h-screen">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Success{' '}
            <span className="bg-gradient-to-r from-[#FF56A5] via-[#E852FF] to-[#9F44F2] bg-clip-text text-transparent">
              Stories
            </span>
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Discover how businesses across industries are transforming their operations with Lifelike Agents
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {useCases.map((useCase, index) => (
            <UseCaseCard key={index} {...useCase} />
          ))}
        </div>

        {/* CTA Section */}
        <div className="max-w-3xl mx-auto">
          <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-1">
            <div className="bg-gradient-to-br from-[#FF56A5]/20 via-[#E852FF]/20 to-[#9F44F2]/20 rounded-2xl p-8 text-center">
              <h2 className="text-2xl font-semibold text-white mb-4">
                Ready to Write Your Success Story?
              </h2>
              <p className="text-gray-300 mb-6">
                Join the growing list of businesses transforming their operations with AI-powered communication.
              </p>
              <Link
                to="/request-demo"
                className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-full text-white bg-gradient-to-r from-[#FF56A5] via-[#E852FF] to-[#9F44F2] hover:from-[#FF56A5]/90 hover:via-[#E852FF]/90 hover:to-[#9F44F2]/90 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                Get Started Today
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default UseCases; 